import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useVehicleMetadata } from "@/hooks/queries";
import type { Vehicle } from "@/types";

// Popup "i" do detalhe do veículo no mapa (pedido 2026-09-26): dados do
// rastreador/chip, cada linha copia o valor ao clicar. Quem pode ver é
// decidido por quem abre (admin sempre, funcionário com
// devices.tracker_info, cliente nunca).
export function VehicleTrackerInfoDialog({
  vehicle: v,
  open,
  onOpenChange,
}: {
  vehicle: Vehicle;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: meta, isLoading } = useVehicleMetadata(v.id);
  const [copied, setCopied] = useState<string | null>(null);

  const tracker = [meta?.trackerBrand, meta?.trackerModel].filter(Boolean).join(" ");
  const rows: { label: string; value: string | null | undefined }[] = [
    { label: "Número da linha", value: meta?.simNumber || v.phone },
    { label: "IMEI", value: v.imei },
    { label: "Operadora", value: meta?.simCarrier },
    { label: "ICCID", value: meta?.simIccid },
    { label: "APN", value: meta?.simApn },
    { label: "Modelo do rastreador", value: tracker || null },
    { label: "Protocolo", value: meta?.protocol || v.protocol },
    { label: "Fornecedor", value: meta?.trackerSupplier },
    { label: "Placa", value: v.plate },
    // Onde o técnico instalou (pedido 2026-10-29: "os funcionários não
    // conseguem ver o local da instalação que os técnicos colocam").
    { label: "Local da instalação", value: meta?.installLocation },
    { label: "Instalador", value: meta?.installTechnician },
    { label: "Data da instalação", value: meta?.installedAt ? meta.installedAt.slice(0, 10).split("-").reverse().join("/") : null },
  ];

  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied((c) => (c === label ? null : c)), 1500);
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Informações do rastreador</DialogTitle>
          <p className="text-xs text-muted-foreground">{v.name} · clique numa informação para copiar</p>
        </DialogHeader>
        <div className="space-y-1.5">
          {rows.map((r) => {
            const value = r.value?.toString().trim();
            const isCopied = copied === r.label;
            return (
              <button
                key={r.label}
                type="button"
                disabled={!value}
                onClick={() => value && copy(r.label, value)}
                className="flex w-full items-center justify-between gap-3 rounded-lg border border-border bg-card px-3 py-2 text-left transition hover:bg-muted disabled:cursor-default disabled:hover:bg-card"
              >
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{r.label}</div>
                  <div className="truncate font-mono text-sm text-foreground">
                    {value || (isLoading ? "…" : "—")}
                  </div>
                </div>
                {value &&
                  (isCopied ? (
                    <span className="flex shrink-0 items-center gap-1 text-[11px] font-medium text-emerald-600">
                      <Check className="h-3.5 w-3.5" /> Copiado
                    </span>
                  ) : (
                    <Copy className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  ))}
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
